import { Injectable, inject } from '@angular/core';
import type { MediaResult, Photo } from '@capacitor/camera';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { Filesystem } from '@capacitor/filesystem';
import { FileRead } from '@freelancer/file-read';
import { Pwa } from '@freelancer/pwa';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import type { UiConfig } from '@freelancer/ui/ui.interface';
import {
  CAMERA_LOADING_DISMISS_PLUGIN_NAME,
  cameraLoadingDismissPlugin,
} from './camera-loading-dismiss.plugin';

export interface CameraPickerOptions {
  quality?: number;
}

const CAP8_MIN_VERSION_MAIN = { major: 8, minor: 1 };
const CAP8_MIN_VERSION_FREIGHTLANCER = { major: 24, minor: 1 };
const DEFAULT_QUALITY = 90;

@Injectable({ providedIn: 'root' })
export class CameraPicker {
  private pwa = inject(Pwa);
  private fileRead = inject(FileRead);
  private uiConfig = inject<UiConfig>(UI_CONFIG);

  async takePhoto(
    options: CameraPickerOptions = {},
  ): Promise<File | undefined> {
    if (!this.pwa.isNative()) {
      return undefined;
    }
    const quality = options.quality ?? DEFAULT_QUALITY;
    if (await this.isCapacitor8()) {
      const result = await Camera.takePhoto({
        quality,
        editable: 'no',
        includeMetadata: true,
      });
      return this.mediaResultToFile(result);
    }

    const photo = await Camera.getPhoto({
      quality,
      allowEditing: false,
      source: CameraSource.Camera,
      resultType: CameraResultType.DataUrl,
    });
    return this.photoToFile(photo);
  }

  async pickFromGallery(
    options: CameraPickerOptions = {},
  ): Promise<File | undefined> {
    if (!this.pwa.isNative()) {
      return undefined;
    }
    const quality = options.quality ?? DEFAULT_QUALITY;
    if (await this.isCapacitor8()) {
      try {
        const { results } = await Camera.chooseFromGallery({
          quality,
          editable: 'no',
          allowMultipleSelection: false,
          includeMetadata: true,
        });
        return results[0] ? this.mediaResultToFile(results[0]) : undefined;
      } finally {
        this.dismissLoadingActivity();
      }
    }

    const photo = await Camera.getPhoto({
      quality,
      allowEditing: false,
      source: CameraSource.Photos,
      resultType: CameraResultType.DataUrl,
    });
    return this.photoToFile(photo);
  }

  private async isCapacitor8(): Promise<boolean> {
    const minVersion =
      this.uiConfig.theme === 'freightlancer'
        ? CAP8_MIN_VERSION_FREIGHTLANCER
        : CAP8_MIN_VERSION_MAIN;
    return this.pwa.hasMinimumNativeAppVersion(minVersion);
  }

  private async mediaResultToFile(
    result: MediaResult,
  ): Promise<File | undefined> {
    if (!result.uri) {
      return undefined;
    }
    const { data } = await Filesystem.readFile({ path: result.uri });
    if (typeof data !== 'string') {
      return undefined;
    }
    const format = result.metadata?.format ?? 'jpeg';
    return this.fileRead.readImageDataIntoFile(
      `data:image/${format};base64,${data}`,
    );
  }

  private photoToFile(photo: Photo): File | undefined {
    return photo.dataUrl
      ? this.fileRead.readImageDataIntoFile(photo.dataUrl)
      : undefined;
  }

  /**
   * Workaround for https://github.com/ionic-team/capacitor-camera/issues/55:
   *
   * We re-send the same broadcast to dismiss to loading page activity.
   */
  private dismissLoadingActivity(): void {
    if (!Capacitor.isPluginAvailable(CAMERA_LOADING_DISMISS_PLUGIN_NAME)) {
      return;
    }
    cameraLoadingDismissPlugin.dismiss().catch(() => undefined);
  }
}
